/**
 * The pure brand and polarity projections, moved from the shell's `platform/applyBranding.ts`, so
 * that the surface host writes the same accent that the chrome reads (kernel apps, WS-13 section
 * 3.2). `resolvePolarity` is new. The DOM writer `applyBranding` moves to `@zion/surface-host`
 * (WS-14). Every function here is pure.
 *
 * Colour values are `#rrggbb` by the contract (`OrgBrandingSchema`). The surface host writes them
 * through `style.setProperty`, where a custom-property value cannot break out into other CSS.
 */
import type { ResolvedPolarity, ShellPolarity } from '@zion/sdk-contracts';
import type { DeploymentBranding, OrgBranding } from '@zion/sdk-cp-dp-contracts';

/** The neutral product name: the tab title and the wordmark when no tier sets `productName`. */
export const NEUTRAL_PRODUCT_NAME = 'Console';

/**
 * The neutral favicon, a muted rounded square. It must stay byte-identical to the
 * `<link rel="icon">` default in the shell's `index.html`.
 */
export const NEUTRAL_FAVICON_HREF: string =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'>" +
  "<rect x='4' y='4' width='24' height='24' rx='7' fill='%23787e91'/></svg>";

/**
 * The accent and the secondary accent for the resolved polarity.
 *
 * Each field reads the org record first, then the deployment record, then `null`. In `dark` the
 * function reads `primaryColorDark` and `accentSecondaryDark`, and never falls back to the light
 * value of the same record. In `light` it reads `primaryColor` and `accentSecondary`.
 */
export function accentFor(
  branding: OrgBranding | null,
  resolved: ResolvedPolarity,
  deployment: DeploymentBranding | null = null,
): { accent: string | null; accentSecondary: string | null } {
  const dark = resolved === 'dark';
  const pick = (o: OrgBranding | DeploymentBranding | null, k: 'primaryColor' | 'accentSecondary'): string | null => {
    if (!o) return null;
    return (dark ? o[`${k}Dark` as const] : o[k]) ?? null;
  };
  return {
    accent: pick(branding, 'primaryColor') ?? pick(deployment, 'primaryColor'),
    accentSecondary: pick(branding, 'accentSecondary') ?? pick(deployment, 'accentSecondary'),
  };
}

/**
 * The foreground on an accent (D23): black or white, whichever has the higher WCAG 2.x contrast
 * against the `#rrggbb` accent, so a solid-accent control stays legible for any tenant colour.
 *
 * @returns `null` for a `null` accent, so the `.on-accent` CSS default applies. `rgb(0 0 0)` for
 *   a value that is not `#rrggbb`. Otherwise `rgb(0 0 0)` or `rgb(255 255 255)`. A tie goes to
 *   black. The answer clears 4.5:1 against every accent. The luminance rule must stay equal to
 *   the CP console's `onAccentFor`; only the output format differs.
 */
export function onAccentFor(accent: string | null): string | null {
  if (accent === null) return null;
  const match = /^#([0-9a-fA-F]{6})$/.exec(accent);
  if (!match) return 'rgb(0 0 0)';
  const n = parseInt(match[1]!, 16);
  const toLinear = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * toLinear((n >> 16) & 0xff) + 0.7152 * toLinear((n >> 8) & 0xff) + 0.0722 * toLinear(n & 0xff);
  const contrastBlack = (L + 0.05) / 0.05;
  const contrastWhite = 1.05 / (L + 0.05);
  return contrastBlack >= contrastWhite ? 'rgb(0 0 0)' : 'rgb(255 255 255)';
}

/**
 * The logo URL for the resolved polarity, or `null` when no tier sets a logo.
 *
 * The org logo comes first, then the deployment logo. In each record, `dark` prefers `darkUrl`
 * and falls back to `lightUrl`, and `light` prefers `lightUrl` and falls back to `darkUrl`.
 */
export function logoUrlFor(
  branding: OrgBranding | null,
  resolved: ResolvedPolarity,
  deployment: DeploymentBranding | null = null,
): string | null {
  const pick = (logo: OrgBranding['logo']): string | null => {
    if (!logo) return null;
    const primary = resolved === 'dark' ? logo.darkUrl : logo.lightUrl;
    const fallback = resolved === 'dark' ? logo.lightUrl : logo.darkUrl;
    return primary ?? fallback ?? null;
  };
  return pick(branding?.logo) ?? pick(deployment?.logo);
}

/**
 * The polarity that a preference resolves to.
 *
 * @returns `preference` when it is `light` or `dark`. For `system`: `dark` when `systemDark` is
 *   `true`, else `light`.
 */
export function resolvePolarity(preference: ShellPolarity, systemDark: boolean): ResolvedPolarity {
  if (preference !== 'system') return preference;
  return systemDark ? 'dark' : 'light';
}
